import { useState, useEffect } from "react";

/**
 * useAnyModalOpen — true whenever a modal / dialog / drawer with a darkening
 * backdrop is mounted anywhere in the document. Used to auto-hide the preview
 * "Demo Tools" floating launcher so it never obscures interactive controls
 * (AI Booking, New Booking, SOS review, Corporate statements, etc.).
 *
 * Detection is structural & convention-based so any future pop-up benefits
 * automatically: our modals render a `fixed inset-0 bg-black/NN` backdrop, and
 * accessible dialogs expose role="dialog"/"alertdialog". Purely presentational.
 */
const MODAL_SELECTOR = '[class*="inset-0"][class*="bg-black/"], [role="dialog"], [role="alertdialog"]';

export function useAnyModalOpen() {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    let raf = 0;
    const check = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        setOpen(!!document.querySelector(MODAL_SELECTOR));
      });
    };
    check();
    const obs = new MutationObserver(check);
    obs.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ["class", "role"],
    });
    return () => {
      cancelAnimationFrame(raf);
      obs.disconnect();
    };
  }, []);

  return open;
}

export default useAnyModalOpen;
